import { createContext, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUnseenReleaseNotes, syncLocalAckWithServer } from 'store/entities/release-notes/release-notes';
import ClientConfig from 'src/config/config';
import type { TAppDispatch, TRootState } from 'src/store/store-types';
import { getAcknowledgedReleaseNotes } from 'views/navigation/new-app-version/release-notes-storage';
import { NewAppVersionModal } from './new-app-version-modal';
import { ReleaseNotesVersionModal } from './release-notes-modal';

interface ReleaseNotesContextValue {
  triggerReleaseNotesFlow: () => void;
}

export const ReleaseNotesContext = createContext<ReleaseNotesContextValue>({
  triggerReleaseNotesFlow: () => {},
});

interface ReleaseNotesProviderProps {
  children: ReactNode;
}

export const ReleaseNotesProvider = ({ children }: ReleaseNotesProviderProps) => {
  const dispatch = useDispatch<TAppDispatch>();

  const unseenNotes = useSelector((state: TRootState) => state.entities.releaseNotes?.unseenNotes ?? []);
  const isLoading = useSelector((state: TRootState) => state.entities.releaseNotes?.isLoading ?? false);
  const hasLoaded = useSelector((state: TRootState) => state.entities.releaseNotes?.hasLoaded ?? false);
  const isNewVersionDetected = useSelector(
    (state: TRootState) => state.entities.releaseNotes?.isNewVersionDetected ?? false,
  );

  const releaseNotesEnabled = ClientConfig.config.enableReleaseNotes;

  useEffect(() => {
    if (!releaseNotesEnabled) return;
    const initialize = async () => {
      const localAck = getAcknowledgedReleaseNotes();
      if (localAck.length > 0) {
        await dispatch(syncLocalAckWithServer(localAck));
      }
      dispatch(fetchUnseenReleaseNotes());
    };

    initialize();
  }, [dispatch, releaseNotesEnabled]);

  const triggerReleaseNotesFlow = () => {
    dispatch(fetchUnseenReleaseNotes());
  };

  const shouldShowModal = releaseNotesEnabled && !isLoading && unseenNotes.length > 0;

  // Fallback for a hotfix / new version with no release note: once the fetch has
  // settled (success or failure) and there are no unseen notes
  const shouldShowFallbackModal =
    releaseNotesEnabled && isNewVersionDetected && !isLoading && hasLoaded && unseenNotes.length === 0;

  return (
    <ReleaseNotesContext.Provider value={{ triggerReleaseNotesFlow }}>
      {children}
      {shouldShowModal && <ReleaseNotesVersionModal />}
      {!shouldShowModal && shouldShowFallbackModal && <NewAppVersionModal />}
    </ReleaseNotesContext.Provider>
  );
};
